import { Component, Host, Prop, h, Method, Event, Element, State } from '@stencil/core';
import { hideFocusRingWhenUsingMouse } from '../../../../global/utils/utils';
import iconClose from '../../../../assets/icon-close.svg';
/** Known issues TODO
 *  1. In screen reader browse mode, pressing the enter key does not fire the close button and the dialog remains
 *  open while the focus is placed back on the modal trigger button
 *  2. Once the modal is opened shift tab ( ie reverse order) will miss the footer buttons
 **/
/**
 * @slot
 * @slot header - Content placed in this slot will be placed at the header section of the modal
 * @slot content - Content placed in this slot will be placed at the body section of the modal
 * @slot footer - Content placed in this slot will be placed at the footer section of the modal
 */
export class ModalComponent {
  constructor() {
    /**
     * Animation type for different modals
     */
    this.animationType = 'slidedown';
    /**
     * Flag to fix the width of Modal. If center screen, the max-width will be 800px and max-height will be 600px
     * @deprecated This prop will be combined with animation type ie slidedown will always be fullwidth
     */
    this.appearance = 'fullwidth'; //TODO remove all references to appearance when the prop is removed
    /**
     * Disable scroll for popup appearance.
     */
    this.disableScroll = false;
    /**
     * Id of modal trigger.
     */
    this.modalTriggerId = '';
    this.disableWhileWait = false;
    this.handleScrollEvent = (event) => {
      this.modalScroll.emit(event);
    };
    this.closeModalWhenClickOutside = (event) => {
      if (this.disableWhileWait) {
        return;
      }
      let insideModalArea;
      if (this.appearance === 'fullwidth') {
        insideModalArea = this.modalWrapper;
      }
      else {
        insideModalArea = this.modalContainer;
      }
      if (!insideModalArea.contains(event.target)) {
        this.closeModal('clickOutside');
      }
    };
    /*
     * When functions move focus around, set this true so the trapfocus listener
     * can ignore the events.
     */
    this.ignoreFocusChanges = false;
    this.trapFocus = (event) => {
      if (this.disableWhileWait) {
        // need to disable all elements within the modal so that user can not use the tab key
        this.disableWhileWaitElement.focus();
      }
      else {
        if (this.ignoreFocusChanges) {
          return;
        }
        if (this.modalContainer.contains(event.target)) {
          this.lastFocus = event.target;
        }
        else {
          this.focusFirstDescendant(this.modalContainer);
          if (this.lastFocus == document.activeElement) {
            this.focusLastDescendant(this.modalContainer);
          }
          this.lastFocus = document.activeElement;
        }
      }
    };
    this.resetContainerHeight = () => {
      this.shade.style.height = `${window.innerHeight}px`;
      const headerStyles = window.getComputedStyle(this.headerElement);
      const headerHeight = parseInt(headerStyles.height) + parseInt(headerStyles.marginTop);
      let footerHeight = 0;
      if (this.footerElement) {
        const footerStyles = window.getComputedStyle(this.footerElement);
        footerHeight = parseInt(footerStyles.height) + parseInt(footerStyles.marginTop);
      }
      this.contentElement.style.maxHeight = this.modalContainer.clientHeight - headerHeight - footerHeight + 'px';
    };
    this.debounce = (callback, wait) => {
      let timeout;
      return (...args) => {
        const context = this;
        clearTimeout(timeout);
        timeout = setTimeout(() => callback.apply(context, args), wait);
      };
    };
    this.debouncedResetContainerHeight = this.debounce(this.resetContainerHeight, 20);
  }
  focusFirstDescendant(element) {
    for (let i = 0; i < element.childNodes.length; i++) {
      const child = element.childNodes[i];
      if (this.attemptFocus(child) || this.focusFirstDescendant(child)) {
        return true;
      }
    }
    return false;
  }
  focusLastDescendant(element) {
    for (let i = element.childNodes.length - 1; i >= 0; i--) {
      const child = element.childNodes[i];
      if (this.attemptFocus(child) || this.focusLastDescendant(child)) {
        return true;
      }
    }
    return false;
  }
  attemptFocus(element) {
    if (!this.isFocusable(element)) {
      return false;
    }
    this.ignoreFocusChanges = true;
    try {
      element.focus();
    }
    catch (e) {
      return false;
    }
    this.ignoreFocusChanges = false;
    return document.activeElement === element;
  }
  isFocusable(element) {
    if (element.tabIndex > 0 || ((element.tabIndex === 0 || element.tabIndex === -1) && element.getAttribute('tabIndex') !== null)) {
      return true;
    }
    if (element.disabled) {
      return false;
    }
    switch (element.nodeName) {
      case 'A':
        return !!element.href && element.rel != 'ignore';
      case 'INPUT':
        return element.type != 'hidden' && element.type != 'file';
      case 'BUTTON':
      case 'SELECT':
      case 'TEXTAREA':
        return true;
      default:
        return false;
    }
  }
  handleButtonKeyDown(e) {
    if (this.disableWhileWait) {
      return;
    }
    const key = e.key || e.keyCode;
    if (key === 'Escape' || key === 'Esc' || key === 27) {
      this.closeModal();
    }
  }
  /**
   * Public method to open the modal. This component requires the trigger element to be passed via the modal.setTriggerButton() method as an anonymous function, so that focus can be returned to it after the modal has closed
   * @param {HTMLElement} el, the element need to be focused after modal close. This parameter is deprecated in favour of the setTriggerButton method
   */
  async openModal(el) {
    var _a, _b;
    //this is here until the deprecated param can be removed
    // eslint-disable-next-line no-self-assign
    el = el;
    if (this.animationType === 'slidedown') {
      this.modalWrapper.classList.add('slide-down');
      this.modalWrapper.classList.remove('slide-up');
    }
    if (((_a = this.modalTrigger) === null || _a === void 0 ? void 0 : _a.tagName.toUpperCase()) === 'AGL-DS-BUTTON') {
      this.modalTrigger.shadowRoot.querySelector('button').setAttribute('aria-expanded', 'true');
    }
    else if (((_b = this.modalTrigger) === null || _b === void 0 ? void 0 : _b.tagName.toUpperCase()) === 'AGL-DS-LINK') {
      //to announce to the screen reader that the trigger is collapsed
      this.modalTrigger.shadowRoot.querySelector('a').setAttribute('aria-expanded', 'true');
    }
    this.modalWrapper.classList.remove('hide');
    this.modalWrapper.classList.add('agl-ds-modal__wrapper--visible');
    this.shade.classList.remove('agl-ds-modal__shade--hidden');
    this.shade.classList.add('agl-ds-modal__shade--visible');
    document.getElementsByTagName('body')[0].style.overflow = 'hidden';
    document.addEventListener('focus', this.trapFocus, true);
    document.addEventListener('click', this.closeModalWhenClickOutside, true);
    document.addEventListener('keydown', this.handleButtonKeyDown.bind(this), true);
    window.addEventListener('resize', this.debouncedResetContainerHeight, true);
    this.resetContainerHeight();
    this.modalOpened.emit();
    this.modalContainer.setAttribute('aria-label', this.getHeaderText());
    setTimeout(() => {
      this.closeInputElement.focus();
    }, 100);
  }
  /**
   * Public method to close the modal
   */
  async closeModal(action = 'manual') {
    var _a, _b, _c;
    if (this.animationType === 'slidedown') {
      this.modalWrapper.classList.add('slide-up');
      this.modalWrapper.classList.remove('slide-down');
    }
    this.modalWrapper.classList.add('hide');
    this.modalWrapper.classList.remove('agl-ds-modal__wrapper--visible');
    this.shade.classList.add('agl-ds-modal__shade--hidden');
    this.shade.classList.remove('agl-ds-modal__shade--visible');
    document.getElementsByTagName('body')[0].style.overflow = 'auto';
    document.removeEventListener('focus', this.trapFocus, true);
    document.removeEventListener('click', this.closeModalWhenClickOutside, true);
    window.removeEventListener('resize', this.debouncedResetContainerHeight, true);
    this.modalClosed.emit(action);
    if (this.modalTrigger) {
      if (((_a = this.modalTrigger) === null || _a === void 0 ? void 0 : _a.tagName.toUpperCase()) === 'AGL-DS-BUTTON') {
        //to announce to the screen reader that the trigger is collapsed
        this.modalTrigger.shadowRoot.querySelector('button').setAttribute('aria-expanded', 'false');
        this.modalTrigger.setFocus();
      }
      else if (((_b = this.modalTrigger) === null || _b === void 0 ? void 0 : _b.tagName.toUpperCase()) === 'AGL-DS-LINK') {
        //to announce to the screen reader that the trigger is collapsed
        this.modalTrigger.shadowRoot.querySelector('a').setAttribute('aria-expanded', 'false');
        this.modalTrigger.setFocus();
      }
      else if (((_c = this.modalTrigger) === null || _c === void 0 ? void 0 : _c.tagName.toUpperCase()) === 'AGL-DS-TEXTBOX') {
        this.modalTrigger.setFocus();
      }
    }
  }
  /**
   * Public method that disables everything in the modal and prevents close by backdrop click or on escape.
   */
  async disabledWhileWait(disabled) {
    this.disableWhileWait = disabled;
  }
  /**
   * Public method to set the modal trigger element
   * @param {Function} getTriggerButton, The anonymous function that returns the element that needs to be focused on after modal close
   */
  async setTriggerButton(getTriggerButton) {
    const checkTrigger = async (fn) => {
      //limit the number of tries. If it does not find the object then an error is logged but the page will render TODO maybe increase the number of tries
      const maxTriesToFindTriggerElement = 200;
      for (let x = 0; x < maxTriesToFindTriggerElement; x++) {
        if (fn() === null) {
          try {
            await new Promise((resolve) => requestAnimationFrame(resolve));
          }
          catch (_a) {
            //here for jest to handle the promise
          }
        }
        else {
          break;
        }
        if (x === maxTriesToFindTriggerElement - 1) {
          console.error('This component requires the trigger element to be passed via the modal.setTriggerButton() method as an anonymous function, so that focus can be returned to it after the modal has closed');
        }
      }
      return fn();
    };
    checkTrigger(getTriggerButton).then((trigger) => {
      var _a, _b;
      this.modalTrigger = trigger;
      if (((_a = this.modalTrigger) === null || _a === void 0 ? void 0 : _a.tagName.toUpperCase()) === 'AGL-DS-BUTTON' || ((_b = this.modalTrigger) === null || _b === void 0 ? void 0 : _b.tagName.toUpperCase()) === 'AGL-DS-LINK') {
        this.modalTrigger.srContext = '(dialog)';
        this.modalTrigger.ariaAttribute = [{ attribute: 'aria-expanded', value: 'false' }];
      }
    });
  }
  componentWillLoad() {
    this.hasFooterSlot = !!this.hostElement.querySelector('[slot="footer"]');
    if (this.animationType === 'slidedown') {
      this.appearance = 'fullwidth';
    }
    else {
      this.appearance = 'centerscreen';
    }
  }
  componentDidLoad() {
    hideFocusRingWhenUsingMouse(this.closeInputElement);
  }
  getHeaderText() {
    return this.headerElement.innerText;
  }
  /**
   * Create HTML representation of component DOM and return this
   for output to the browser DOM
   */
  render() {
    return (h(Host, null,
      h("div", { tabIndex: 0 }),
      h("div", { class: {
          hide: true,
          'agl-ds-modal__wrapper': true,
          'agl-ds-modal__wrapper--center-screen': this.appearance === 'centerscreen',
          'agl-ds-modal__full-screen': this.appearance === 'fullwidth'
        }, ref: (el) => (this.modalWrapper = el) },
        h("div", { class: {
            'agl-ds-modal__container': true,
            'agl-ds-modal__container--center-screen': this.appearance === 'centerscreen',
            'agl-ds-modal__container--disable-scroll': this.disableScroll
          }, ref: (el) => (this.modalContainer = el), role: "dialog" },
          h("div", { role: "document" },
            h("div", { class: "agl-ds-modal__header-container" },
              this.disableWhileWait && (h("div", { tabindex: 0, class: "agl-ds-modal__header-container--disable-while-wait", ref: (el) => (this.disableWhileWaitElement = el) })),
              h("button", { ref: (el) => (this.closeInputElement = el), class: {
                  'agl-ds-modal__close-button': true,
                  'agl-ds-modal__close-button--center-screen': this.appearance === 'centerscreen'
                }, onClick: () => {
                  this.closeModal('iconButton');
                } },
                h("agl-ds-icon", { icon: iconClose, size: "xxs", "alt-text": "Close the modal dialog" })),
              h("div", { ref: (el) => (this.headerElement = el), class: {
                  'agl-ds-modal__heading': true,
                  'agl-ds-modal__heading--center-screen': this.appearance === 'centerscreen'
                } },
                h("slot", { name: "header" }))),
            h("div", { ref: (el) => (this.contentElement = el), class: {
                'agl-ds-modal__content': true,
                'agl-ds-modal__content--center-screen': this.appearance === 'centerscreen'
              }, onScroll: (event) => {
                this.handleScrollEvent(event);
              } },
              h("slot", { name: "content" })),
            this.hasFooterSlot && (h("div", { ref: (el) => (this.footerElement = el), class: {
                'agl-ds-modal__footer': true,
                'agl-ds-modal__footer--center-screen': this.appearance === 'centerscreen'
              } },
              h("slot", { name: "footer" }))))),
        h("div", { tabIndex: 0 })),
      h("div", { class: "agl-ds-modal__shade", ref: (el) => (this.shade = el) })));
  }
  static get is() { return "agl-ds-modal"; }
  static get encapsulation() { return "scoped"; }
  static get originalStyleUrls() { return {
    "$": ["modal.component.scss"]
  }; }
  static get styleUrls() { return {
    "$": ["modal.component.css"]
  }; }
  static get properties() { return {
    "animationType": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "AnimationType",
        "resolved": "\"popup\" | \"slidedown\"",
        "references": {
          "AnimationType": {
            "location": "import",
            "path": "./modal.types"
          }
        }
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Animation type for different modals"
      },
      "attribute": "animation-type",
      "reflect": false,
      "defaultValue": "'slidedown'"
    },
    "appearance": {
      "type": "string",
      "mutable": true,
      "complexType": {
        "original": "ModalAppearance",
        "resolved": "\"centerscreen\" | \"fullwidth\"",
        "references": {
          "ModalAppearance": {
            "location": "import",
            "path": "./modal.types"
          }
        }
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [{
            "text": "This prop will be combined with animation type ie slidedown will always be fullwidth",
            "name": "deprecated"
          }],
        "text": "Flag to fix the width of Modal. If center screen, the max-width will be 800px and max-height will be 600px"
      },
      "attribute": "appearance",
      "reflect": false,
      "defaultValue": "'fullwidth'"
    },
    "disableScroll": {
      "type": "boolean",
      "mutable": false,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Disable scroll for popup appearance."
      },
      "attribute": "disable-scroll",
      "reflect": false,
      "defaultValue": "false"
    },
    "modalTriggerId": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Id of modal trigger."
      },
      "attribute": "modal-trigger-id",
      "reflect": false,
      "defaultValue": "''"
    }
  }; }
  static get states() { return {
    "disableWhileWait": {}
  }; }
  static get events() { return [{
      "method": "modalOpened",
      "name": "modalOpened",
      "bubbles": true,
      "cancelable": true,
      "composed": true,
      "docs": {
        "tags": [],
        "text": "Fires when modal is opened"
      },
      "complexType": {
        "original": "void",
        "resolved": "void",
        "references": {}
      }
    }, {
      "method": "modalClosed",
      "name": "modalClosed",
      "bubbles": true,
      "cancelable": true,
      "composed": true,
      "docs": {
        "tags": [],
        "text": "Fires when modal is closed."
      },
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      }
    }, {
      "method": "modalScroll",
      "name": "modalScroll",
      "bubbles": true,
      "cancelable": true,
      "composed": true,
      "docs": {
        "tags": [],
        "text": "Fires when the onScroll event"
      },
      "complexType": {
        "original": "UIEvent",
        "resolved": "UIEvent",
        "references": {
          "UIEvent": {
            "location": "global"
          }
        }
      }
    }]; }
  static get methods() { return {
    "openModal": {
      "complexType": {
        "signature": "(el: HTMLElement) => Promise<void>",
        "parameters": [{
            "tags": [{
                "text": "el , the element need to be focused after modal close. This parameter is deprecated in favour of the setTriggerButton method",
                "name": "param"
              }],
            "text": ", the element need to be focused after modal close. This parameter is deprecated in favour of the setTriggerButton method"
          }],
        "references": {
          "Promise": {
            "location": "global"
          },
          "HTMLElement": {
            "location": "global"
          }
        },
        "return": "Promise<void>"
      },
      "docs": {
        "text": "Public method to open the modal. This component requires the trigger element to be passed via the modal.setTriggerButton() method as an anonymous function, so that focus can be returned to it after the modal has closed",
        "tags": [{
            "name": "param",
            "text": "el , the element need to be focused after modal close. This parameter is deprecated in favour of the setTriggerButton method"
          }]
      }
    },
    "closeModal": {
      "complexType": {
        "signature": "(action?: ModalCloseAction) => Promise<void>",
        "parameters": [{
            "tags": [],
            "text": ""
          }],
        "references": {
          "Promise": {
            "location": "global"
          },
          "ModalCloseAction": {
            "location": "import",
            "path": "./modal.types"
          },
          "HTMLAglDsButtonElement": {
            "location": "global"
          },
          "HTMLAglDsLinkElement": {
            "location": "global"
          }
        },
        "return": "Promise<void>"
      },
      "docs": {
        "text": "Public method to close the modal",
        "tags": []
      }
    },
    "disabledWhileWait": {
      "complexType": {
        "signature": "(disabled: boolean) => Promise<void>",
        "parameters": [{
            "tags": [],
            "text": ""
          }],
        "references": {
          "Promise": {
            "location": "global"
          }
        },
        "return": "Promise<void>"
      },
      "docs": {
        "text": "Public method that disables everything in the modal and prevents close by backdrop click or on escape.",
        "tags": []
      }
    },
    "setTriggerButton": {
      "complexType": {
        "signature": "(getTriggerButton: () => HTMLElement) => Promise<void>",
        "parameters": [{
            "tags": [{
                "text": "getTriggerButton , The anonymous function that returns the element that needs to be focused on after modal close",
                "name": "param"
              }],
            "text": ", The anonymous function that returns the element that needs to be focused on after modal close"
          }],
        "references": {
          "Promise": {
            "location": "global"
          },
          "HTMLElement": {
            "location": "global"
          },
          "HTMLAglDsButtonElement": {
            "location": "global"
          }
        },
        "return": "Promise<void>"
      },
      "docs": {
        "text": "Public method to set the modal trigger element",
        "tags": [{
            "name": "param",
            "text": "getTriggerButton , The anonymous function that returns the element that needs to be focused on after modal close"
          }]
      }
    }
  }; }
  static get elementRef() { return "hostElement"; }
}
